import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { ArrowLeft, Lock, Sparkles, Activity, Smartphone, Palette, Check, Play, X } from "lucide-react";
import { MICRO_ACTION_CATEGORIES, MICRO_ACTIONS } from "../data/microActions";
import { useAuth } from "../lib/auth-context";
import { api } from "../lib/api";
import { toast } from "sonner";

const ICON_MAP = { Sparkles, Activity, Smartphone, Palette };

const MicroActionsLibrary = () => {
  const navigate = useNavigate();
  const { isPro } = useAuth();
  const [active, setActive] = useState("declutter");
  const [logs, setLogs] = useState([]);

  const loadLogs = async () => {
    try {
      const r = await api.listMicroActionLogs();
      setLogs(r.data.items || []);
    } catch { /* silent */ }
  };

  useEffect(() => { loadLogs(); }, []);

  const handleSelectCategory = (id) => {
    if (!isPro && id !== "declutter") {
      toast("Categories are a Pro feature", { description: "Free shows declutter only — upgrade for full library." });
      return;
    }
    setActive(id);
  };

  const findLog = (action) => logs.find((l) => l.action_id === action.id && l.date === new Date().toISOString().slice(0,10));

  const setStatus = async (action, status) => {
    try {
      await api.logMicroAction({
        action_id: action.id,
        category: action.category,
        title: action.title,
        status,
      });
      await loadLogs();
      toast.success(`Marked ${status}`);
    } catch (e) {
      const msg = e.response?.data?.detail || "Could not update";
      toast.error(msg);
    }
  };

  const visible = MICRO_ACTIONS.filter((a) => a.category === active);

  return (
    <div className="app-shell" data-testid="micro-actions-page">
      <Header />
      <section className="px-6 pt-2 pb-6">
        <button onClick={() => navigate(-1)} className="text-xs text-[#5C7568] flex items-center gap-1 mb-3" data-testid="back-btn">
          <ArrowLeft className="w-3 h-3" /> Back
        </button>
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#234B38]">Micro-Actions</p>
        <h2 className="text-3xl font-medium text-[#1A2E24] mt-1 leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
          One small thing
        </h2>
        <p className="text-sm text-[#5C7568] mt-2">
          {isPro
            ? "Pick a category, then choose an action."
            : "Free shows the Declutter category. Pro unlocks the full library and history."}
        </p>

        <div className="grid grid-cols-2 gap-2 mt-5" data-testid="category-grid">
          {MICRO_ACTION_CATEGORIES.map((c) => {
            const Icon = ICON_MAP[c.icon] || Sparkles;
            const locked = !isPro && c.id !== "declutter";
            const isActive = active === c.id;
            return (
              <button
                key={c.id}
                data-testid={`ma-cat-${c.id}`}
                disabled={locked}
                onClick={() => handleSelectCategory(c.id)}
                className={`rounded-2xl border p-3 text-left transition-all ${
                  isActive
                    ? "bg-[#234B38] text-white border-[#234B38]"
                    : locked
                    ? "bg-[#FDFBF7] border-[#E2EBE6] cursor-not-allowed opacity-80"
                    : "bg-white border-[#E2EBE6] hover:-translate-y-0.5"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Icon className={`w-4 h-4 ${isActive ? "text-white" : "text-[#234B38]"}`} />
                  {locked && <Lock className="w-3 h-3 text-[#5C7568]" />}
                </div>
                <p className={`text-sm font-medium mt-1.5 ${isActive ? "text-white" : "text-[#1A2E24]"}`}>
                  {c.label}
                </p>
              </button>
            );
          })}
        </div>

        <div className="mt-6 space-y-3" data-testid="action-list">
          {visible.map((a) => {
            const log = findLog(a);
            const status = log?.status;
            return (
              <div key={a.id} className="rounded-3xl bg-white border border-[#E2EBE6] p-5" data-testid={`ma-item-${a.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-medium text-[#1A2E24]" style={{ fontFamily: "Outfit, sans-serif" }}>
                      {a.title}
                    </h3>
                    <p className="text-sm text-[#5C7568] mt-1 leading-relaxed">{a.body}</p>
                  </div>
                  {status === "completed" && (
                    <span className="shrink-0 rounded-full bg-[#234B38] text-white w-8 h-8 flex items-center justify-center">
                      <Check className="w-4 h-4" />
                    </span>
                  )}
                </div>
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => setStatus(a, "started")}
                    data-testid={`ma-start-${a.id}`}
                    className={`flex-1 rounded-full py-2 text-xs font-semibold flex items-center justify-center gap-1.5 ${
                      status === "started" ? "bg-[#F5D0D6] text-[#D6336C]" : "bg-white border border-[#E2EBE6] text-[#5C7568]"
                    }`}
                  >
                    <Play className="w-3 h-3" /> {status === "started" ? "Started" : "Start"}
                  </button>
                  <button
                    onClick={() => setStatus(a, "completed")}
                    data-testid={`ma-complete-${a.id}`}
                    className={`flex-1 rounded-full py-2 text-xs font-semibold flex items-center justify-center gap-1.5 ${
                      status === "completed" ? "bg-[#234B38] text-white" : "bg-white border border-[#E2EBE6] text-[#5C7568]"
                    }`}
                  >
                    <Check className="w-3 h-3" /> Done
                  </button>
                  <button
                    onClick={() => setStatus(a, "skipped")}
                    data-testid={`ma-skip-${a.id}`}
                    className={`flex-1 rounded-full py-2 text-xs font-semibold flex items-center justify-center gap-1.5 ${
                      status === "skipped" ? "bg-[#E2EBE6] text-[#5C7568]" : "bg-white border border-[#E2EBE6] text-[#5C7568]"
                    }`}
                  >
                    <X className="w-3 h-3" /> Skip
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {isPro && logs.length > 0 && (
          <div className="mt-8" data-testid="ma-history">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#234B38] mb-3">History</p>
            <div className="space-y-2">
              {logs.slice(0, 10).map((l) => (
                <div key={l.id} className="rounded-2xl bg-white border border-[#E2EBE6] px-4 py-3 flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-sm text-[#1A2E24] truncate">{l.title}</p>
                    <p className="text-[10px] text-[#5C7568] uppercase tracking-wider">{l.date} · {l.status}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default MicroActionsLibrary;
